import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerUser } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { UserPlus, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c0b7ab56-dc9b-46c6-8c6a-5be1ac8c634e/artifacts/1y3fjfg2_AMAZONIA%20-%20LOGO%402x.png";

export default function Register() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', company_name: '', vat_number: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Les mots de passe ne correspondent pas'); return; }
    if (form.password.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères'); return; }
    setLoading(true);
    try {
      const res = await registerUser({ email: form.email, company_name: form.company_name, vat_number: form.vat_number, password: form.password });
      login(res.data.token, res.data.user);
      setRegistered(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    }
    setLoading(false);
  };

  if (registered) {
    return (
      <div data-testid="register-success" className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg text-center">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
          <h1 className="text-2xl font-extrabold text-[#111] mb-3">Candidature enregistrée !</h1>
          <p className="text-sm text-black/60 mb-8 leading-relaxed">
            Votre candidature est en cours de validation.<br />
            Réservez votre premier rendez-vous commercial pour activer votre compte.
          </p>
          {/* Calendly Embed */}
          <div className="border border-black/10 p-6 mb-6 text-left">
            <h3 className="text-sm font-bold text-[#111] mb-3">Prendre rendez-vous</h3>
            <iframe
              src="https://calendly.com/question-amazonia/30min?primary_color=00a5ff"
              width="100%"
              height="600"
              frameBorder="0"
              title="Calendly"
              className="border-0"
            />
          </div>
          <Link to="/catalog" className="text-sm text-[#00A5FF] font-semibold hover:underline">
            Parcourir le catalogue en attendant
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="register-page" className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Amazonia" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-[#111] tracking-tight">Créer un compte Pro</h1>
          <p className="text-sm text-black/50 mt-1">Accédez aux prix professionnels et commandez en ligne</p>
        </div>

        <div className="border border-black/10 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Email *</label>
              <input
                type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                data-testid="register-email"
                className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Nom de l'entreprise *</label>
              <input
                type="text" required value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})}
                data-testid="register-company"
                className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Numéro de TVA</label>
              <input
                type="text" placeholder="BE0123456789" value={form.vat_number} onChange={(e) => setForm({...form, vat_number: e.target.value})}
                data-testid="register-vat"
                className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Mot de passe *</label>
              <input
                type="password" required value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                data-testid="register-password"
                className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Confirmer le mot de passe *</label>
              <input
                type="password" required value={form.confirm} onChange={(e) => setForm({...form, confirm: e.target.value})}
                data-testid="register-confirm"
                className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
              />
            </div>
            {error && <p data-testid="register-error" className="text-sm text-[#E8231B]">{error}</p>}
            <button
              type="submit" disabled={loading}
              data-testid="register-submit"
              className="w-full flex items-center justify-center gap-2 py-3 bg-[#00A5FF] text-white font-semibold text-sm rounded-sm hover:bg-[#0090e0] disabled:opacity-50 transition-colors duration-200"
            >
              <UserPlus size={16} />
              {loading ? 'Inscription...' : 'Créer mon compte'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-sm text-black/50">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-[#00A5FF] font-semibold hover:underline">Se connecter</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
